import React, { ChangeEvent, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Compressor from 'compressorjs';
import {
    Avatar,
    Box,
    Button,
    Center,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input
} from '@chakra-ui/react';

import { ProfileContext } from '../../../core/contexts';
import UserService, { UserClass } from '../../../core/user';
import ProfileService, { ProfileClass } from '../../../core/profile';

export default function ProfileForm(): React.ReactElement {
    const { t } = useTranslation();
    const userService = new UserService();
    const publicUserService = new ProfileService();

    const { user, setUser, setUserData } = useContext(ProfileContext);

    const [isSaving, setIsSaving] = useState(false);
    const [userPicture, setUserPicture] = useState<Record<string, any>>({});
    const [userError, setUserError] = useState({
        name: false
    });

    const isValid = (): boolean => {
        const isValid = user.name?.length;
        setUserError({
            name: !user.name
        });
        return !!isValid;
    };

    const uploadImage = async (file: File, user: UserClass) => {
        return new Promise((resolve, reject) => {
            new Compressor(file, {
                quality: 0.8,
                maxWidth: 1920,
                maxHeight: 1920,
                async success(result: File) {
                    const saved = (await userService.uploadProfilePicture(
                        result,
                        user
                    )) as UserClass;
                    resolve(saved);
                },
                error(err) {
                    console.error(err.message);
                    reject();
                }
            });
        });
    };

    const updateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isValid()) {
            setIsSaving(true);
            let saved = user;
            if (userPicture.url?.length) {
                saved = (await uploadImage(
                    userPicture.file,
                    saved
                )) as UserClass;
            }
            saved = (await userService.save(saved)) as UserClass;
            publicUserService.save(new ProfileClass(saved));
            setUser(saved);
            setIsSaving(false);
        }
    };

    return (
        <form id='profileForm' onSubmit={updateProfile} noValidate>
            <Flex
                direction={{ base: 'column', lg: 'row' }}
                align={'center'}
                justify={'space-around'}
            >
                <Flex direction={'column'}>
                    <Avatar
                        bg='white'
                        size='2xl'
                        name={user.name}
                        src={userPicture.url || user.getImage()}
                    />

                    <Button
                        as='label'
                        size='xs'
                        bg={'green.600'}
                        colorScheme='green'
                        my='2'
                    >
                        {t('form.changePhoto')}
                        <Input
                            type='file'
                            defaultValue=''
                            accept='image/*'
                            capture='environment'
                            display={'none'}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setUserPicture({
                                        file: file as File,
                                        url: URL.createObjectURL(file)
                                    });
                                }
                            }}
                        />
                    </Button>
                </Flex>
                <Box>
                    <FormControl isRequired isInvalid={userError.name} my='2'>
                        <FormLabel htmlFor='name' m='0'>
                            {t('form.name')}
                        </FormLabel>
                        <Input
                            id='name'
                            type='text'
                            defaultValue={user.name}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setUserData('name', e.target.value);
                            }}
                        />
                        <FormErrorMessage>
                            {t('error.required')}
                        </FormErrorMessage>
                    </FormControl>

                    <FormControl my='2'>
                        <FormLabel htmlFor='email' m='0'>
                            {t('form.email')}
                        </FormLabel>
                        <Input
                            id='email'
                            type='text'
                            defaultValue={user.email}
                            isDisabled={true}
                        />
                        <FormHelperText>
                            El email no puede ser cambiado
                        </FormHelperText>
                    </FormControl>
                </Box>
            </Flex>
            <Center mt='2'>
                <Button
                    colorScheme='green'
                    variant='outline'
                    size='sm'
                    type='submit'
                    form='profileForm'
                    isLoading={isSaving}
                >
                    {t('form.save')}
                </Button>
            </Center>
        </form>
    );
}
